/* eslint-disable react/display-name */
import {
  makeStyles,
  Theme,
  Typography,
  Checkbox,
  createStyles,
  Badge,
} from "@material-ui/core";
import * as React from "react";
import TreeItem, { TreeItemProps } from "@material-ui/lab/TreeItem";
import TextField from "@material-ui/core/TextField";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import {
  TreeDataType,
  StructureType,
  PathToNodeName,
  RemoveNode,
  RenameNode,
  ExistsNode,
  ToggleDisplaySelf,
  RenameExpanded,
  GetExcludeNodeNames,
  ActionNameAdd,
  ActionNameRename,
  IsCategoryNode,
  IsSelfOrProgeny,
  PathToParentPath,
} from "./model/TreeItemModel";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelRoot: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0, 0),
    },
    labelText: {
      fontWeight: "inherit",
      flexGrow: 1,
      margin: 0,
      padding: 0,
    },
    labelLoadingIcon: {
      color: theme.palette.text.disabled,
      fontSize: 18,
    },
    labelTextCategory: {
      fontWeight: "bold",
      flexGrow: 1,
    },
    inputMarginDense: {
      padding: "4px 8px",
    },
    customBadge: {
      color: "white",
      backgroundColor: "#9e9e9e",
    },
    customBadgeZero: {
      color: "white",
      backgroundColor: "#9e9e9e",
      opacity: 0.5,
    },
    // カテゴリーノードスタイル
    // ・ホバー時・フォーカス時に背景色を変更しない
    // ・カーソルをポインターにしない
    treeItemCategoryRoot: {
      "&:focus > $treeItemCategoryContent $treeItemCategoryLabel": {
        backgroundColor: "transparent",
      },
    },
    treeItemCategoryLabel: {
      cursor: "default",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    treeItemCategoryContent: {},
  })
);

interface Props extends TreeItemProps {
  configId: number;
  parent: StructureType | undefined;
  count: number;
  isDisplayAll: boolean;
  isDisplaySelf: boolean;
  isDisplayInputText: boolean;
  isDummyNode: boolean;
  editNodeInfo: EditNodeInfoType;
  getTreeDataById: (id: number | undefined) => TreeDataType | undefined;
  setTreeDataById: (
    id: number | undefined,
    structure: StructureType[] | undefined,
    expandedNode: string[] | undefined,
    isDisplayAll: boolean | undefined,
    update: boolean
  ) => void;
  handleClickNode: (
    event: React.MouseEvent<HTMLElement>,
    node: string,
    configId: number,
    excludes?: string[]
  ) => void;
  setRenderNodeStates: (updateRenderNodeId: string) => void;
  handleTreeContextMenuClick: (
    event: React.MouseEvent<HTMLElement>,
    node: string
  ) => void;
}

// ============================
// 編集中ノード情報

/** 編集中ノード情報定義 */
export type EditNodeInfoType = {
  selectedNodeId: string;
  editNodeId: string;
  treeEditMode: string;
  renderNodeId: string;
};
/** 編集中ノード情報初期値 */
export const initialEditNodeInfo = {
  selectedNodeId: "",
  editNodeId: "",
  treeEditMode: "",
  renderNodeId: "",
};

/** 仮想フォルダ―ツリーアイテム */
export const VirtualFolderTreeItem = React.memo(
  (props: Props) => {
    const classes = useStyles(props);
    const {
      configId,
      parent,
      nodeId,
      label,
      count,
      children,
      isDummyNode,
      editNodeInfo,
      isDisplayAll,
      isDisplaySelf,
      isDisplayInputText,
      getTreeDataById,
      setTreeDataById,
      handleClickNode,
      setRenderNodeStates,
      handleTreeContextMenuClick,
    } = props;

    // console.log(nodeId);

    //------------------------------
    // [State]

    // 編集中のノードの名前
    const [editName, setEditName] = React.useState<string>(
      editNodeInfo.treeEditMode === ActionNameAdd ? "" : PathToNodeName(nodeId)
    );
    // 編集中のノードの入力エラー内容
    const [editNameErrMsg, setEditNameErrMsg] = React.useState<string>("");

    //------------------------------
    // [Handler]

    /** 新規フォルダ―作成(フォルダ―確定) */
    const handleAddTreeNodeRegist = () => {
      const tree = getTreeDataById(configId);
      if (!tree) return;

      const parentPath: string = editNodeInfo.selectedNodeId;
      const newPath =
        parentPath === "" ? editName : `${parentPath}/${editName}`;
      const existEditNodeName = ExistsNode(tree.structure, newPath);
      const existDummyPath = ExistsNode(tree.structure, nodeId);

      // 入力が空白か、既に存在する名前の場合、ダミーフォルダーを削除する
      if (editName === "" || existEditNodeName) {
        const removedNode = RemoveNode(tree.structure, nodeId);
        setTreeDataById(configId, removedNode, undefined, undefined, false);
      } else if (existDummyPath) {
        // 入力があれば、ダミーフォルダーのpathと名前を変更する
        const renamedNode = RenameNode(tree.structure, nodeId, editName);
        // ステートのセット、API連携、ローカルストレージセット
        setTreeDataById(configId, renamedNode, undefined, undefined, true);
      }
      // 編集用ノードのレンダー用の更新(新規追加フォルダ―の親フォルダ―以下を更新)
      setRenderNodeStates(parentPath);
    };

    /** フォルダ―名変更(フォルダ―確定) */
    const handleRenameTreeNodeRegist = () => {
      const tree = getTreeDataById(configId);
      if (!tree) return;

      const isSameName =
        PathToNodeName(editNodeInfo.selectedNodeId) === editName;
      const existEditNodeName = ExistsNode(tree.structure, nodeId);
      // 入力が空白か、変更前の名前と同じ場合、なにもしない
      if (editName === "" || isSameName) {
        // なにもしない
      } else if (existEditNodeName) {
        // 名前の変更があれば、pathと名前を変更する
        const renamedNode = RenameNode(tree.structure, nodeId, editName);
        // 展開ノードのパスも合わせて変更する
        const renamedExpanded = RenameExpanded(
          tree.expandedNode,
          nodeId,
          editName
        );
        // ステートのセット、API連携、ローカルストレージセット
        setTreeDataById(
          configId,
          renamedNode,
          renamedExpanded,
          undefined,
          true
        );
      }
      // 編集ノードのステートクリア
      const parentPath = PathToParentPath(nodeId);
      const renamedPath =
        parentPath === "" ? editName : `${parentPath}/${editName}`;
      // 編集用ノードのレンダー用の更新(自分のフォルダ―以下を更新)
      setRenderNodeStates(renamedPath);
    };

    /** 編集中の名前の入力保持 */
    const handleChangeEditNodeName = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      event.preventDefault();
      event.stopPropagation();
      setEditName(event.target.value);
    };

    /** 編集中の名前の入力チェックと入力確定 */
    const editNodeNameRegist = () => {
      // 入力チェック
      // 禁止文字を含んだらエラー
      const ngChar = "/";
      const regexNgChar = RegExp(`[/]`);
      if (regexNgChar.test(editName)) {
        setEditNameErrMsg(`次の文字は使用できません。(${ngChar})`);
        return;
      }
      // 桁数を超えたらエラー
      const maxLength = 20;
      if (editName.length > maxLength) {
        setEditNameErrMsg(`${maxLength}桁以内で入力してください。`);
        return;
      }
      setEditNameErrMsg("");

      // フォルダ―名確定の各イベント実行
      if (editNodeInfo.treeEditMode === ActionNameAdd) {
        handleAddTreeNodeRegist();
      } else if (editNodeInfo.treeEditMode === ActionNameRename) {
        handleRenameTreeNodeRegist();
      }
    };

    /** 編集中の名前のサブミット */
    const handleSubmitEditNodeName = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      // エンターキーとタブキー入力時のみ処理する
      if (event.which !== 13 && event.which !== 9) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      editNodeNameRegist();
    };

    /** 編集中の名前のサブミット(フォーカスが外れた場合) */
    const handleOnClickAwayEditNodeName = (
      event: React.MouseEvent<Document, MouseEvent>
    ) => {
      event.preventDefault();
      event.stopPropagation();
      editNodeNameRegist();
    };

    /** ノードの表示切替 */
    const handleChangeCheckbox = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      event.stopPropagation();
      const { checked } = event.target;
      const tree = getTreeDataById(configId);
      if (!tree) return;

      const toggledNode = ToggleDisplaySelf(tree.structure, nodeId, checked);
      setTreeDataById(configId, toggledNode, undefined, undefined, false);

      // 編集用ノードのレンダー用の更新(自分のフォルダ―以下と祖先フォルダ―を更新)
      setRenderNodeStates(nodeId);
    };

    //------------------------------
    // コンポーネントの描画

    /** ツリーの開閉を遅延表示しない設定 */
    const transitionProps = { timeout: 0 };

    /** カウントの表示／非表示の判定に使用する */
    const hasCount = count >= 0;

    /** ノードの表示／非表示切替用 */
    const checkBox = (
      <Checkbox
        id={nodeId}
        checked={isDisplaySelf}
        size="small"
        style={{ padding: 0 }}
        color="primary"
        disabled={isDummyNode}
        disableRipple
        onChange={handleChangeCheckbox}
        onFocus={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      />
    );

    const getLabelClass = (nodeIdValue: string) => {
      return IsCategoryNode(nodeIdValue)
        ? classes.labelTextCategory
        : classes.labelText;
    };

    const labelBase = (
      <>
        {isDisplayAll && checkBox}
        <Typography
          variant="body2"
          className={getLabelClass(nodeId)}
          onClick={e =>
          handleClickNode(e, nodeId, configId, GetExcludeNodeNames(nodeId, parent))}
        >
          {label}
        </Typography>
      </>
    );

    /** ノードの表示ラベル（件数あり） */
    const labelCount = (
      <div className={classes.labelRoot}>
        {labelBase}
        <Badge
          badgeContent={count}
          max={999}
          showZero
          classes={{
            badge: count === 0 ? classes.customBadgeZero : classes.customBadge,
          }}
        />
      </div>
    );

    /** ノードの表示ラベル（件数取得中） */
    const labelLoading = (
      <div className={classes.labelRoot}>
        {labelBase}
        <AutorenewIcon className={classes.labelLoadingIcon} />
      </div>
    );

    /** ノードの表示ラベル（件数なし） */
    const labelNormal = <div className={classes.labelRoot}>{labelBase}</div>;

    // テキストフィールドの初期フォーカス対応
    const inputRef: any = React.useRef();
    React.useEffect(() => {
      let cleanedUp = false;

      const timeout = setTimeout(() => {
        if (!cleanedUp) {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }
      }, 300);

      return function cleanup() {
        clearTimeout(timeout);
        cleanedUp = true;
      };
    });

    /** ノードのテキスト表示（入力エラーなし） */
    const txtName = (
      <ClickAwayListener onClickAway={handleOnClickAwayEditNodeName}>
        <OutlinedInput
          id="standard-basic"
          margin="dense"
          autoFocus
          placeholder={label?.toString() || ""}
          // value={editName}
          value={editName}
          onChange={handleChangeEditNodeName}
          // onKeyDown=>onBlurは勝手に後続処理呼ばれるのでNG
          onKeyDown={handleSubmitEditNodeName}
          // onClick=>テキストボックス表示後、クリックするとフォーカスが外れる現象の対応
          onClick={event => event.stopPropagation()}
          inputRef={inputRef}
          classes={{
            inputMarginDense: classes.inputMarginDense,
          }}
        />
      </ClickAwayListener>
    );

    /** ノードのテキスト表示（入力エラーあり） */
    const txtNameError = (
      <ClickAwayListener onClickAway={handleOnClickAwayEditNodeName}>
        <TextField
          error
          id="outlined-error-helper-text"
          variant="outlined"
          margin="dense"
          autoFocus
          label="入力エラー"
          value={editName}
          helperText={editNameErrMsg}
          onChange={handleChangeEditNodeName}
          onKeyDown={handleSubmitEditNodeName}
          InputProps={{
            classes: {
              inputMarginDense: classes.inputMarginDense,
            },
          }}
        />
      </ClickAwayListener>
    );

    // フォルダ―名入力領域表示
    if (isDisplayInputText) {
      // 初期表示(エラー表示なし)
      if (editNameErrMsg === "") {
        return (
          <TreeItem
            key={nodeId}
            nodeId={nodeId}
            label={txtName}
            TransitionProps={transitionProps}
          >
            {props.children}
          </TreeItem>
        );
      }
      // エラー表示状態
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          label={txtNameError}
          TransitionProps={transitionProps}
        >
          {props.children}
        </TreeItem>
      );
    }
    // 件数表示あり
    if (hasCount) {
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          label={labelCount}
          TransitionProps={transitionProps}
          onContextMenu={e => handleTreeContextMenuClick(e, nodeId)}
        >
          {children}
        </TreeItem>
      );
    }
    // 件数表示なし（カテゴリノード）
    if (IsCategoryNode(nodeId)) {
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          label={labelNormal}
          TransitionProps={transitionProps}
          onContextMenu={e => handleTreeContextMenuClick(e, nodeId)}
          classes={{
            root: classes.treeItemCategoryRoot,
            content: classes.treeItemCategoryContent,
            label: classes.treeItemCategoryLabel,
          }}
        >
          {children}
        </TreeItem>
      );
    }
    // 件数表示なし（件数取得中）
    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={labelLoading}
        TransitionProps={transitionProps}
        onContextMenu={e => handleTreeContextMenuClick(e, nodeId)}
      >
        {children}
      </TreeItem>
    );
  },

  (prevProps, nextProps) => {
    // console.log(nextProps.editNodeInfo.renderNodeId);
    const { renderNodeId } = nextProps.editNodeInfo;
    return (
      renderNodeId !== "" && !IsSelfOrProgeny(renderNodeId, nextProps.nodeId)
    );
  }
);
